import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import type { HistDataPoint } from './priceStatistic';

interface ChartProps {
    data: {
        area: string;
        currentSuggestedPrice: number;
        averageSuggestedPrice: number;
        sales: HistDataPoint[];
    };
    isMobile: boolean;
}

export function Chart({ data, isMobile }: ChartProps) {
    const [isMounted, setIsMounted] = useState(false);
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const { sales, currentSuggestedPrice, averageSuggestedPrice, area } = data;
    const diff = Math.abs(averageSuggestedPrice - currentSuggestedPrice);
    const hoveredIndexRef = useRef<number | null>(null);

    useEffect(() => {
        Highcharts.setOptions({
            lang: { thousandsSep: ' ', decimalPoint: ',' },
        });
        import('highcharts/modules/accessibility').then((mod) => {
            const accessibility = mod.default ?? mod;
            accessibility(Highcharts);
            setIsMounted(true);
        });
    }, []);

    // Disable Highcharts' broken pointer tracking in shadow DOM
    useEffect(() => {
        const chart = chartRef.current?.chart;
        if (!chart) return;
        const container = chart.container;
        container.style.overflow = 'visible';
        if (container.parentElement) {
            container.parentElement.style.overflow = 'visible';
        }
        // Remove all Highcharts pointer event handlers from the container
        container.onmousemove = null;
        container.onmousedown = null;
        container.onclick = null;
        const pointer = chart.pointer as any;
        if (pointer.unDocMouseMove) {
            pointer.unDocMouseMove();
            pointer.unDocMouseMove = undefined;
        }
        // Remove hcEvents listeners that cause tooltip reset
        const hcContainer = container as any;
        if (hcContainer.hcEvents) {
            delete hcContainer.hcEvents.mouseenter;
            delete hcContainer.hcEvents.mouseleave;
        }
    }, [isMounted]);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const chart = chartRef.current?.chart;
        if (!chart || !chart.series[0]) return;

        const container = chart.container;
        const rect = container.getBoundingClientRect();
        const chartX = e.clientX - rect.left - chart.plotLeft;
        const chartY = e.clientY - rect.top - chart.plotTop;

        if (chartX < 0 || chartX > chart.plotWidth || chartY < 0 || chartY > chart.plotHeight) {
            if (hoveredIndexRef.current !== null) {
                const prevPoint = chart.series[0].points[hoveredIndexRef.current];
                prevPoint?.onMouseOut();
                chart.xAxis[0].removePlotLine('hover-line');
                chart.tooltip.hide();
                hoveredIndexRef.current = null;
            }
            return;
        }

        const pointIndex = Math.floor((chartX / chart.plotWidth) * chart.series[0].points.length);
        const clampedIndex = Math.max(0, Math.min(pointIndex, chart.series[0].points.length - 1));
        const point = chart.series[0].points[clampedIndex];

        if (!point || point.y === 0) {
            if (hoveredIndexRef.current !== null) {
                const prevPoint = chart.series[0].points[hoveredIndexRef.current];
                prevPoint?.onMouseOut();
                chart.xAxis[0].removePlotLine('hover-line');
                chart.tooltip.hide();
                hoveredIndexRef.current = null;
            }
            return;
        }

        if (hoveredIndexRef.current !== clampedIndex) {
            if (hoveredIndexRef.current !== null) {
                const prevPoint = chart.series[0].points[hoveredIndexRef.current];
                prevPoint?.onMouseOut();
                chart.xAxis[0].removePlotLine('hover-line');
            }
            point.onMouseOver();
            chart.xAxis[0].addPlotLine({
                id: 'hover-line',
                value: clampedIndex,
                color: '#3291ED',
                width: 2,
                dashStyle: 'Dash',
                zIndex: 4,
            });
            hoveredIndexRef.current = clampedIndex;
        }

        chart.tooltip.refresh(point);
    }, []);

    const handleMouseLeave = useCallback(() => {
        const chart = chartRef.current?.chart;
        if (!chart || !chart.series[0]) return;
        if (hoveredIndexRef.current !== null) {
            const prevPoint = chart.series[0].points[hoveredIndexRef.current];
            prevPoint?.onMouseOut();
            chart.xAxis[0].removePlotLine('hover-line');
            chart.tooltip.hide();
            hoveredIndexRef.current = null;
        }
    }, []);

    const { allCategories, seriesData } = useMemo(() => {
        const minPrice = Math.min(...sales.map((s) => s.sqmPrice)) - 2000;
        const maxPrice = Math.max(...sales.map((s) => s.sqmPrice)) + 2000;
        const cats: number[] = [];
        for (let p = minPrice; p < maxPrice; p += 1000) {
            cats.push(p);
        }
        const dataMap = new Map(sales.map((s) => [s.sqmPrice, s.numberOfSales]));
        const sData = cats.map((p) => dataMap.get(p) ?? 0);
        return { allCategories: cats, seriesData: sData };
    }, [sales]);

    const options = useMemo<Highcharts.Options>(() => {
        return {
            chart: {
                type: 'column',
                height: 300,
                backgroundColor: 'transparent',
                style: { fontFamily: 'Finntype' },
                scrollablePlotArea: isMobile ? { minWidth: 600, scrollPositionX: 0 } : undefined,
            },
            title: { text: undefined },
            legend: { enabled: false },
            credits: { enabled: false },
            accessibility: {
                description: `Histogram som viser prisfordeling for boliger i ${area}. Denne boligen koster ${currentSuggestedPrice} kr per kvadratmeter, mens gjennomsnittet er ${averageSuggestedPrice} kr per kvadratmeter`,
            },
            xAxis: {
                categories: allCategories.map((p) => p.toLocaleString('nb-no')),
                labels: {
                    step: Math.floor(allCategories.length / 4),
                    style: { color: '#767676', fontSize: '12px' },
                },
                lineColor: '#767676',
                tickLength: 0,
                title: {
                    text: 'Pris per m² i kr',
                    align: 'high',
                    style: { fontFamily: 'Finntype', fontWeight: 'bold', fontSize: '14px', color: '#767676' },
                },
                plotLines: [
                    {
                        color: '#474445',
                        width: 2,
                        value: allCategories.indexOf(currentSuggestedPrice),
                        zIndex: 5,
                        label: {
                            text: `Denne boligen<br/><b>${currentSuggestedPrice.toLocaleString('nb-no')} kr</b>`,
                            useHTML: true,
                            rotation: 0,
                            x: currentSuggestedPrice > averageSuggestedPrice ? 5 : -105,
                            style: { fontFamily: 'Finntype', color: '#474445', fontSize: '14px' },
                        },
                    },
                    ...(diff > 1000
                        ? [
                              {
                                  color: '#474445',
                                  width: 2,
                                  dashStyle: 'Dash' as Highcharts.DashStyleValue,
                                  value: allCategories.indexOf(averageSuggestedPrice),
                                  zIndex: 5,
                                  label: {
                                      text: `Gjennomsnitt<br/><b>${averageSuggestedPrice.toLocaleString('nb-no')} kr</b>`,
                                      useHTML: true,
                                      rotation: 0,
                                      x: averageSuggestedPrice > currentSuggestedPrice ? 5 : -95,
                                      style: { fontFamily: 'Finntype', color: '#474445', fontSize: '14px' },
                                  },
                              },
                          ]
                        : []),
                ],
            },
            yAxis: {
                title: { text: null },
                labels: { enabled: false },
                gridLineWidth: 0,
            },
            series: [
                {
                    type: 'column',
                    name: 'Antall salg',
                    data: seriesData,
                    color: '#3291ED',
                    borderRadius: 2,
                    borderWidth: 0,
                },
            ],
            plotOptions: {
                column: {
                    groupPadding: 0,
                    pointPadding: 0,
                    states: {
                        hover: { color: '#3291ED', brightness: 0 },
                        inactive: { opacity: 0.5 },
                    },
                },
            },
            tooltip: {
                useHTML: true,
                backgroundColor: '#1F2937',
                borderWidth: 0,
                borderRadius: 8,
                style: { color: '#FFFFFF', fontFamily: 'Finntype' },
                formatter() {
                    const sqmPrice = allCategories[this.point.index];
                    return `<div style="padding:8px;font-size:16px">
                        <span><strong>${this.y}</strong> boliger</span><br/>
                        <span style="font-size:14px"><strong>${sqmPrice.toLocaleString('nb-no')} - ${(sqmPrice + 1000).toLocaleString('nb-no')}</strong> kr</span>
                    </div>`;
                },
            },
        };
    }, [sales, currentSuggestedPrice, averageSuggestedPrice, area, diff, isMobile, allCategories, seriesData]);

    if (!isMounted) {
        return (
            <div className="flex flex-col">
                <p className="text-14 s-text-subtle font-bold">Antall boliger</p>
                <div style={{ height: 300 }} />
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            <p className="text-14 s-text-subtle font-bold">Antall boliger</p>
            <div onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
                <HighchartsReact
                    highcharts={Highcharts}
                    options={options}
                    ref={chartRef}
                    containerProps={{ style: { width: '100%' } }}
                />
            </div>
        </div>
    );
}
